import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  UserPlus,
  Search,
  MoreHorizontal,
  Edit,
  Shield,
  KeyRound,
  Power,
  Trash2,
  Loader2,
  RefreshCw,
  Mail,
  Send,
  Unlock,
  Lock,
  Copy,
} from "lucide-react";
import type { ApiResponse, RoleDetail } from "@/types";

interface UserRecord {
  id: string | number;
  username: string;
  first_name?: string;
  last_name?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  mobile?: string;
  status: boolean;
  role: string;
  user_role_id?: string | number;
  user_role?: any;
  manager_id?: string;
  manager?: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
  };
  created_on?: string;
  /** ACTIVE: has chosen a password · INVITED: invite link still valid · INVITE_EXPIRED: needs a new invite */
  invite_status?: "ACTIVE" | "INVITED" | "INVITE_EXPIRED";
  invite_expires_at?: string | null;
  /** Sign-in temporarily locked after too many wrong passwords */
  locked?: boolean;
}

/** A one-time link to hand to a user (shown once; the server keeps only a scrambled copy) */
type SharedLink = { kind: "invite" | "reset"; email: string; url: string; expires_at: string; emailed: boolean };

export function UsersPage() {
  const [roles, setRoles] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [addManagerLabel, setAddManagerLabel] = useState("");
  const [roleManagerLabel, setRoleManagerLabel] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");

  // Dialog States
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRecord | null>(null);
  const [changeRoleUser, setChangeRoleUser] = useState<UserRecord | null>(null);
  const [sharedLink, setSharedLink] = useState<SharedLink | null>(null);
  const [deleteUser, setDeleteUser] = useState<UserRecord | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [addForm, setAddForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    user_name: "",
    mobile: "",
    user_role_id: "",
    manager_id: "",
    send_welcome_email: true,
  });

  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    user_name: "",
    mobile: "",
  });

  const [newRoleId, setNewRoleId] = useState("");
  const [newManagerId, setNewManagerId] = useState("none");

  const { success, error } = useToast();

  useEffect(() => {
    api
      .get("/api/UserRole/List")
      .then((res) => {
        const rawRoles = unwrapArray<RoleDetail>(res);
        setRoles(
          rawRoles.filter(
            (r) => (r.code || "").toUpperCase() !== "SUPER_ADMIN" && (r.name || "").toLowerCase() !== "super admin",
          ),
        );
      })
      .catch((err: any) => error("Fetch Error", err.message || "Failed to load roles"));
  }, []);

  const list = usePagedList<UserRecord>({
    deps: [search, selectedRole],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      if (selectedRole !== "all") qs.set("role_id", selectedRole);
      try {
        return unwrapPage<UserRecord>(await api.get(`/api/User/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load user list");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const filteredUsers = list.rows;
  const loading = list.loading;
  const loadData = list.reload;

  const isSalesExecRole = (roleId: string) =>
    (roles.find((r: any) => String(r.id || r.Id) === String(roleId))?.code || "").toUpperCase() === "SALES_EXEC";

  const salesHeadRoleId = String(roles.find((r: any) => (r.code || "").toUpperCase() === "SALES_HEAD")?.id || "");

  const managerName = (row: any) => {
    const name = `${row.first_name || row.firstName || ""} ${row.last_name || row.lastName || ""}`.trim() || row.username;
    const roleName = row.user_role?.name || row.role || "User";
    return `${name} (${roleName})`;
  };

  // Handle Add User: the user gets an invite link to choose their own password
  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.email || !addForm.first_name || !addForm.user_role_id) {
      error("Validation Error", "Please fill all required fields");
      return;
    }
    if (isSalesExecRole(addForm.user_role_id) && !addForm.manager_id) {
      error("Validation Error", "A Sales Executive must report to a Sales Head");
      return;
    }

    setActionLoading(true);
    try {
      const res: any = await api.post("/api/User/Insert", {
        first_name: addForm.first_name,
        last_name: addForm.last_name || undefined,
        email: addForm.email,
        username: addForm.user_name || addForm.email.split("@")[0],
        mobile: addForm.mobile || undefined,
        user_role_id: addForm.user_role_id,
        manager_id: addForm.manager_id || undefined,
        send_welcome_email: addForm.send_welcome_email,
      });
      const created = res?.AddtionalData || {};
      success("User Created", created.emailed ? "Invite emailed" : "Share the invite link with the user");
      if (created.invite_url) {
        setSharedLink({
          kind: "invite",
          email: addForm.email,
          url: created.invite_url,
          expires_at: created.invite_expires_at,
          emailed: !!created.emailed,
        });
      }
      setAddOpen(false);
      setAddForm({
        first_name: "",
        last_name: "",
        email: "",
        user_name: "",
        mobile: "",
        user_role_id: "",
        manager_id: "",
        send_welcome_email: true,
      });
      await loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not create user");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Edit User
  const handleEditOpen = (u: UserRecord) => {
    setEditUser(u);
    setEditForm({
      first_name: u.first_name || u.firstName || "",
      last_name: u.last_name || u.lastName || "",
      email: u.email,
      user_name: u.username,
      mobile: u.mobile || "",
    });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;

    setActionLoading(true);
    try {
      const { user_name, ...rest } = editForm;
      await api.put(`/api/User/Update/${editUser.id}`, { ...rest, username: user_name });
      success("User Updated", "User details saved successfully");
      setEditUser(null);
      await loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Failed to update user");
    } finally {
      setActionLoading(false);
    }
  };

  // Toggle Suspend / Activate
  const handleToggleStatus = async (u: UserRecord) => {
    try {
      const res = await api.patch(`/api/User/SuspendOrActivate/${u.id}`);
      success("Status Updated", res.message || "User status updated");
      await loadData();
    } catch (err: any) {
      error("Action Failed", err.message || "Failed to toggle status");
    }
  };

  // Handle Update Role & Manager
  const handleRoleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changeRoleUser || !newRoleId) return;
    if (isSalesExecRole(newRoleId) && newManagerId === "none") {
      error("Validation Error", "A Sales Executive must report to a Sales Head");
      return;
    }

    setActionLoading(true);
    try {
      await api.patch(`/api/User/UpdateRole/${changeRoleUser.id}`, {
        user_role_id: newRoleId,
        manager_id: newManagerId === "none" ? null : newManagerId,
      });
      success("Role & Reporting Updated", "User security role and reporting manager updated successfully");
      setChangeRoleUser(null);
      setNewRoleId("");
      setNewManagerId("none");
      await loadData();
    } catch (err: any) {
      error("Role Update Failed", err.message || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  // Invite / reset links: the server emails them and returns them once, so the admin can also share them
  const handleSendLink = async (u: UserRecord, kind: "invite" | "reset") => {
    setActionLoading(true);
    try {
      const res: any =
        kind === "invite"
          ? await api.post(`/api/User/ResendInvite/${u.id}`)
          : await api.post(`/api/User/SendPasswordReset/${u.id}`);
      const data = res?.AddtionalData || {};
      setSharedLink({
        kind,
        email: u.email,
        url: data.invite_url || data.reset_url,
        expires_at: data.invite_expires_at || data.reset_expires_at,
        emailed: !!data.emailed,
      });
      await loadData();
    } catch (err: any) {
      error("Could not create link", err.message || "Please try again");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlock = async (u: UserRecord) => {
    try {
      await api.patch(`/api/User/Unlock/${u.id}`);
      success("Sign-in unlocked", `${u.email} can sign in again`);
      await loadData();
    } catch (err: any) {
      error("Unlock failed", err.message || "Please try again");
    }
  };

  const copyLink = async () => {
    if (!sharedLink) return;
    try {
      await navigator.clipboard.writeText(sharedLink.url);
      success("Link copied", "Send it to the user privately");
    } catch {
      error("Copy failed", "Select the link and copy it manually");
    }
  };

  // Handle Delete
  const handleDelete = async () => {
    if (!deleteUser) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/User/Delete/${deleteUser.id}`);
      success("User Removed", "User account deleted successfully");
      setDeleteUser(null);
      await loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Failed to delete user");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            User Directory
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage user accounts, assign security roles, and control active status
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="h-9 gap-1.5"
          >
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setAddOpen(true)}
            className="h-9 gap-1.5 shadow-sm"
          >
            <UserPlus className="size-4" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input
            placeholder="Search by name, email, or username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9 text-sm"
          />
        </div>
        <div className="w-full sm:w-56">
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue placeholder="Filter by Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Security Roles</SelectItem>
              {roles.map((r: any) => (
                <SelectItem key={r.id || r.Id} value={String(r.id || r.Id)}>
                  {r.name || r.RoleName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data Table */}
      {loading && filteredUsers.length === 0 ? (
        <TableSkeleton rows={5} columns={5} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User Account</TableHead>
                <TableHead>Email & Contact</TableHead>
                <TableHead>Assigned Role</TableHead>
                <TableHead>Reporting Manager</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map((u) => {
                const fullName = `${u.first_name || u.firstName || ""} ${u.last_name || u.lastName || ""}`.trim() || u.username;
                const initials = fullName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2);

                const roleDisplay = u.user_role?.name || u.user_role?.RoleName || u.role || "User";

                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="size-9 border border-border">
                          <AvatarFallback>{initials || "U"}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-semibold text-sm text-foreground leading-tight">
                            {fullName}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono">
                            @{u.username}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <p className="text-sm text-foreground">{u.email}</p>
                      {u.mobile && (
                        <p className="text-xs text-muted-foreground font-mono">
                          {u.mobile}
                        </p>
                      )}
                    </TableCell>

                    <TableCell>
                      <Badge variant="blue" className="text-xs font-medium">
                        {roleDisplay}
                      </Badge>
                    </TableCell>

                    <TableCell>
                      {u.manager ? (
                        <div className="text-xs">
                          <span className="font-semibold text-foreground block">
                            {u.manager.first_name} {u.manager.last_name}
                          </span>
                          <span className="text-muted-foreground font-mono text-[11px]">
                            {u.manager.email}
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">None (Direct)</span>
                      )}
                    </TableCell>

                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.status === false ? (
                          <Badge variant="destructive" className="text-xs">
                            Suspended
                          </Badge>
                        ) : u.invite_status === "INVITED" ? (
                          <Badge
                            variant="warning"
                            className="text-xs"
                            title={u.invite_expires_at ? `Invite expires ${new Date(u.invite_expires_at).toLocaleString()}` : undefined}
                          >
                            Invited
                          </Badge>
                        ) : u.invite_status === "INVITE_EXPIRED" ? (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Invite expired
                          </Badge>
                        ) : (
                          <Badge variant="success" className="text-xs">
                            Active
                          </Badge>
                        )}
                        {u.locked && (
                          <Badge variant="destructive" className="text-xs" title="Too many wrong passwords">
                            <Lock className="size-3 mr-1" />
                            Locked
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem
                            onClick={() => handleEditOpen(u)}
                            className="cursor-pointer"
                          >
                            <Edit className="size-4 mr-2" />
                            Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setChangeRoleUser(u);
                              setNewRoleId(String(u.user_role_id || u.user_role?.id || ""));
                              setNewManagerId(u.manager_id || "none");
                              setRoleManagerLabel(
                                u.manager
                                  ? `${u.manager.first_name || ""} ${u.manager.last_name || ""}`.trim()
                                  : "",
                              );
                            }}
                            className="cursor-pointer"
                          >
                            <Shield className="size-4 mr-2" />
                            Change Role
                          </DropdownMenuItem>
                          {u.invite_status && u.invite_status !== "ACTIVE" ? (
                            <DropdownMenuItem
                              onClick={() => handleSendLink(u, "invite")}
                              disabled={u.status === false || actionLoading}
                              className="cursor-pointer"
                            >
                              <Send className="size-4 mr-2" />
                              Resend Invite
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => handleSendLink(u, "reset")}
                              disabled={u.status === false || actionLoading}
                              className="cursor-pointer"
                            >
                              <KeyRound className="size-4 mr-2" />
                              Send Password Reset Link
                            </DropdownMenuItem>
                          )}
                          {u.locked && (
                            <DropdownMenuItem onClick={() => handleUnlock(u)} className="cursor-pointer">
                              <Unlock className="size-4 mr-2" />
                              Unlock Sign-in
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleToggleStatus(u)}
                            className="cursor-pointer"
                          >
                            <Power className="size-4 mr-2" />
                            {u.status !== false ? "Suspend Account" : "Activate Account"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteUser(u)}
                            className="cursor-pointer text-red-600 dark:text-red-400 focus:text-red-600"
                          >
                            <Trash2 className="size-4 mr-2" />
                            Delete Account
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">
                    No users matching criteria.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Dialog: Add User */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with an assigned system security role
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">First Name *</Label>
                <Input
                  required
                  placeholder="John"
                  value={addForm.first_name}
                  onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Last Name</Label>
                <Input
                  placeholder="Doe"
                  value={addForm.last_name}
                  onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Email Address *</Label>
              <Input
                required
                type="email"
                placeholder="john.doe@company.com"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Username</Label>
                <Input
                  placeholder="johndoe"
                  value={addForm.user_name}
                  onChange={(e) => setAddForm({ ...addForm, user_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Mobile Phone</Label>
                <Input
                  placeholder="+1 (555) 000-0000"
                  value={addForm.mobile}
                  onChange={(e) => setAddForm({ ...addForm, mobile: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Security Role *</Label>
              <Select
                value={addForm.user_role_id}
                onValueChange={(val) => setAddForm({ ...addForm, user_role_id: val })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r: any) => (
                    <SelectItem key={r.id || r.Id} value={String(r.id || r.Id)}>
                      {r.name || r.RoleName} ({r.code || r.RoleCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">
                {isSalesExecRole(addForm.user_role_id) ? "Reports To (Sales Head) *" : "Reporting Manager (Optional)"}
              </Label>
              <SearchSelect
                value={addForm.manager_id}
                selectedLabel={addManagerLabel}
                endpoint="/api/User/List"
                extraParams={isSalesExecRole(addForm.user_role_id) && salesHeadRoleId ? { role_id: salesHeadRoleId } : {}}
                placeholder="Search users"
                allowEmpty={!isSalesExecRole(addForm.user_role_id)}
                emptyLabel="None (Top Level / Direct)"
                getLabel={managerName}
                filterRow={(row) => row.status !== false}
                onChange={(id, row) => {
                  setAddManagerLabel(row ? managerName(row) : "");
                  setAddForm({ ...addForm, manager_id: id });
                }}
              />
            </div>

            {/* Email Dispatch Checkbox Card */}
            <div className="flex items-start gap-3 p-3.5 rounded-xl border border-blue-200 bg-blue-50/50">
              <input
                type="checkbox"
                id="send_welcome_email"
                checked={addForm.send_welcome_email}
                onChange={(e) => setAddForm({ ...addForm, send_welcome_email: e.target.checked })}
                className="mt-0.5 size-4 rounded text-primary focus:ring-primary cursor-pointer"
              />
              <label htmlFor="send_welcome_email" className="text-xs sm:text-sm cursor-pointer select-none">
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <Mail className="size-4 text-primary" />
                  Email the invite to the user
                </span>
                <span className="text-muted-foreground block text-[11px] sm:text-xs mt-0.5 leading-relaxed">
                  Sends <strong>{addForm.email || "the user"}</strong> a link to choose their own password (valid 72 hours). Nobody else
                  ever knows it. Either way, you'll also get the link to share yourself.
                </span>
              </label>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit User */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
            <DialogDescription>
              Update basic account information and contact numbers
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">First Name</Label>
                <Input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Last Name</Label>
                <Input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm">Email Address</Label>
              <Input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Username</Label>
                <Input
                  value={editForm.user_name}
                  onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs sm:text-sm">Mobile</Label>
                <Input
                  value={editForm.mobile}
                  onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                />
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setEditUser(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Change Role & Reporting Manager */}
      <Dialog open={!!changeRoleUser} onOpenChange={(open) => !open && setChangeRoleUser(null)}>
        <DialogContent className="sm:max-w-lg md:max-w-xl">
          <DialogHeader>
            <DialogTitle>Update Security Role & Hierarchy</DialogTitle>
            <DialogDescription>
              Assign role privileges and reporting manager for {changeRoleUser?.first_name || changeRoleUser?.username}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRoleSubmit} className="space-y-4 py-2 text-xs sm:text-sm">
            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">Assigned Security Role *</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r: any) => (
                    <SelectItem key={r.id || r.Id} value={String(r.id || r.Id)}>
                      {r.name || r.RoleName} ({r.code || r.RoleCode})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs sm:text-sm font-semibold">
                {isSalesExecRole(newRoleId) ? "Reports To (Sales Head) *" : "Reporting Manager"}
              </Label>
              <SearchSelect
                value={newManagerId === "none" ? "" : newManagerId}
                selectedLabel={roleManagerLabel}
                endpoint="/api/User/List"
                extraParams={isSalesExecRole(newRoleId) && salesHeadRoleId ? { role_id: salesHeadRoleId } : {}}
                placeholder="Search users"
                allowEmpty={!isSalesExecRole(newRoleId)}
                emptyLabel="None (Top Level / Direct)"
                getLabel={managerName}
                filterRow={(row) => String(row.id) !== String(changeRoleUser?.id) && row.status !== false}
                onChange={(id, row) => {
                  setRoleManagerLabel(row ? managerName(row) : "");
                  setNewManagerId(id || "none");
                }}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setChangeRoleUser(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading || !newRoleId}>
                {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
                Save Role & Manager
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: one-time invite / reset link */}
      <Dialog open={!!sharedLink} onOpenChange={(open) => !open && setSharedLink(null)}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{sharedLink?.kind === "invite" ? "Invite link" : "Password reset link"}</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              {sharedLink?.emailed ? "We emailed this link to " : "Send this link to "}
              <strong className="text-foreground">{sharedLink?.email}</strong>
              {sharedLink?.emailed
                ? ". You can also share it yourself, privately."
                : " yourself, privately. It was not emailed: no company mailbox is connected under Emails → Email Connection (or sending failed)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="flex gap-2">
              <Input readOnly value={sharedLink?.url || ""} className="font-mono text-xs sm:text-sm h-10 sm:h-11" onFocus={(e) => e.currentTarget.select()} />
              <Button type="button" onClick={copyLink} className="shrink-0 h-10 sm:h-11 px-4">
                <Copy className="size-4 mr-1.5" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Works once and expires {sharedLink?.expires_at ? new Date(sharedLink.expires_at).toLocaleString() : ""}. It is shown only
              now: the CRM keeps just a scrambled copy, so close this and use{" "}
              {sharedLink?.kind === "invite" ? "Resend Invite" : "Send Password Reset Link"} if you need it again. Anyone with the
              link can set this account's password, so don't post it in a group chat.
            </p>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setSharedLink(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Delete Confirmation */}
      <Dialog open={!!deleteUser} onOpenChange={(open) => !open && setDeleteUser(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg text-red-600">Delete User Account</DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Are you sure you want to permanently delete account{" "}
              <strong className="text-foreground">{deleteUser?.email}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-3">
            <Button type="button" variant="outline" onClick={() => setDeleteUser(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={actionLoading}
            >
              {actionLoading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              Confirm Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
